"use client";

import { useState, useEffect, useRef } from "react";
import { Search } from "lucide-react";
import InputField from "@/components/common/InputField";
import Link from "next/link";
import { SearchProduct } from "@/packages/types/products";
import { fetchSearchSuggestion } from "@/api-service/product";
import Price from "./Price";
import { useFastRouter } from "@/packages/hooks/useFastRouter";
import NextImage from "./NextImage";
interface SearchBoxProps {
  onResultSelect?: () => void;
}

const placeholders = [
  "Search for traditional Indian brass utensils...",
  "Search for pure copper water bottles...",
  "Search for traditional bronze cookware...",
  "Search for authentic Indian kitchenware...",
  "Search for heritage cookware and servers..."
];

export default function SearchBox({ onResultSelect }: SearchBoxProps) {
  const [searchText, setSearchText] = useState("");
  const [suggestions, setSuggestions] = useState<SearchProduct[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLDivElement | null>(null);
  const { fastPush } = useFastRouter();

  // Running animated placeholder text
  const [placeholder, setPlaceholder] = useState("");
  const [currentPlaceholderIdx, setCurrentPlaceholderIdx] = useState(0);
  const [isDeleting, setIsDeleting] = useState(false);
  const [typingSpeed, setTypingSpeed] = useState(100);

  useEffect(() => {
    let timer: ReturnType<typeof setTimeout>;
    const fullText = placeholders[currentPlaceholderIdx];

    const handleTyping = () => {
      if (!isDeleting) {
        setPlaceholder(fullText.substring(0, placeholder.length + 1));
        setTypingSpeed(75);

        if (placeholder === fullText) {
          timer = setTimeout(() => setIsDeleting(true), 2500);
          return;
        }
      } else {
        setPlaceholder(fullText.substring(0, placeholder.length - 1));
        setTypingSpeed(35);

        if (placeholder === "") {
          setIsDeleting(false);
          setCurrentPlaceholderIdx((prev) => (prev + 1) % placeholders.length);
          setTypingSpeed(100);
          return;
        }
      }

      timer = setTimeout(handleTyping, typingSpeed);
    };

    timer = setTimeout(handleTyping, typingSpeed);
    return () => clearTimeout(timer);
  }, [placeholder, isDeleting, currentPlaceholderIdx, typingSpeed]);

  // Debounce typing search
  useEffect(() => {
    if (searchText.length < 3) {
      setSuggestions([]);
      setTotalCount(0);
      return;
    }

    const timeout = setTimeout(async () => {
      try {
        const res = await fetchSearchSuggestion(searchText);
        setSuggestions(res.data.products || []);
        setTotalCount(res.data.totalItems || 0);
        setShowDropdown(true);
      } catch (err) {
        console.error("Search error:", err);
      }
    }, 300);

    return () => clearTimeout(timeout);
  }, [searchText]);

  const handleViewAll = () => {
    if (searchText.length < 3) {
      setSuggestions([]);
      setShowDropdown(true); // show dropdown message
      return;
    }
    setShowDropdown(false);
    setSearchText("");
    onResultSelect?.();

    fastPush(`/search-grid/${encodeURIComponent(searchText)}`);
  };

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target as Node)
      ) {
        setShowDropdown(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleViewAll();
    }
  };

  return (
    <div className="relative w-full" ref={dropdownRef}>
      <div ref={inputRef} className="relative w-full">
        <InputField
          name="search"
          placeholder={placeholder}
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          onKeyDown={handleKeyDown}
          className="pr-10 py-2 bg-[rgba(148,145,145,0.2)] border-none text-sm w-full h-[40px] text-[#1F2937]"
        />
        <Search
          className="absolute right-4 top-1/2 -translate-y-1/2 text-primary w-5 h-5 cursor-pointer"
          onClick={handleViewAll}
          onMouseEnter={handleViewAll}
        />
      </div>

      {showDropdown && (
        <div className="absolute top-full left-1/2 -translate-x-1/2 mt-2 bg-white shadow-lg rounded-lg border border-gray-200 z-50 w-full sm:w-[400px]">
          {searchText?.length < 3 ? (
            <div className="p-4 text-center text-gray-500 text-sm">
              Need at least 3 characters
            </div>
          ) : suggestions?.length > 0 ? (
            <>
              <div className="flex flex-col p-2">
                {suggestions.slice(0, 4).map((item) => (
                  <Link
                    href={`/products/${item.slug}`}
                    key={item.id}
                    className="flex items-center gap-4 p-3 hover:bg-gray-50 rounded transition"
                    onClick={() => {
                      setShowDropdown(false);
                      setSearchText("");
                      onResultSelect?.();
                    }}
                  >
                    <div className="w-[80px] h-[80px] bg-gray-100 flex items-center justify-center overflow-hidden rounded">
                      <NextImage
                        src={
                          item?.images?.[0]?.thumbnailUrl?.trim() ||
                          "/assets/site/images/default.png"
                        }
                        alt={item.title}
                        width={80}
                        height={80}
                        className="object-contain w-full h-full"
                      />
                    </div>

                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-gray-800 truncate">
                        {(item.title ?? "").length > 30
                          ? (item.title ?? "").slice(0, 30) + "..."
                          : item.title}
                      </p>
                    </div>

                    <div className="flex-shrink-0">
                      <p className="text-primary font-semibold text-sm">
                        <Price amount={item.salePrice} />
                      </p>
                    </div>
                  </Link>
                ))}
              </div>

              <div
                className="border-t border-gray-300 text-center py-2 cursor-pointer"
                onClick={handleViewAll}
              >
                View all {totalCount} results
              </div>
            </>
          ) : (
            <div className="p-4 text-center text-gray-500 text-sm">
              No results found
            </div>
          )}
        </div>
      )}
    </div>
  );
}
